/**
 * Service for managing predictive spend settings
 */

import { auth } from "./auth-service";

const VITE_CORE_API_URL = import.meta.env.VITE_CORE_API_URL;

/**
 * Asset Type specific settings (most granular level)
 */
export interface AssetTypeSettings {
	usefulLife?: number; // Years
	replacementCost?: number; // USD
	installationDeliveryFactor?: number; // Decimal (0.15 = 15%)
	extendedLifeEligible?: boolean; // Can use Extended Replacement strategy
}

/**
 * Asset Family settings with optional type-level overrides
 */
export interface AssetFamilySettings {
	usefulLife?: number; // Family-level default (years)
	replacementCost?: number; // Family-level default (USD)
	installationDeliveryFactor?: number; // Family-level default (0.15 = 15%)
	assetTypes?: Record<string, AssetTypeSettings>; // Type-specific overrides
}

/**
 * Complete Predictive Spend Settings with hierarchical structure
 */
export interface PredictiveSpendSettings {
	annualInflationRate?: number; // Percentage (e.g., 3.5 for 3.5%)
	assetFamilies?: Record<string, AssetFamilySettings>; // Family name -> settings

	// Legacy flat structure (backward compatibility)
	usefulLifeOverrides?: Record<string, number>; // DEPRECATED
	replacementCostOverrides?: Record<string, number>; // DEPRECATED
}

/**
 * Get predictive spend settings for an organization
 */
export const getPredictiveSpendSettings = async (
	organizationId: number,
): Promise<PredictiveSpendSettings> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/utility/config`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to fetch predictive spend settings: ${errorDetails.message || "Unknown error"}`,
			);
		}

		const result = await response.json();

		// Extract predictive spend settings from organization config
		const config = result.data?.config;
		let settings =
			config?.predictive_spend_settings ||
			config?.predictiveSpendSettings ||
			{};

		// Handle case where settings might be double-stringified (from old bug)
		if (typeof settings === "string") {
			try {
				settings = JSON.parse(settings);
			} catch (e) {
				console.error("Failed to parse predictive spend settings:", e);
				settings = {};
			}
		}

		return settings;
	} catch (exception: any) {
		throw new Error(
			exception.message || "Failed to fetch predictive spend settings",
		);
	}
};

/**
 * Update predictive spend settings for an organization
 */
export const updatePredictiveSpendSettings = async (
	organizationId: number,
	settings: PredictiveSpendSettings,
): Promise<void> => {
	try {
		const url = `${VITE_CORE_API_URL}/core/utility/config`;

		const response = await fetch(url, {
			method: "PUT",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				predictiveSpendSettings: settings,
			}),
		});

		if (response.status !== 200) {
			const errorDetails = await response.json();
			throw new Error(
				`Failed to update predictive spend settings: ${errorDetails.message || "Unknown error"}`,
			);
		}
	} catch (exception: any) {
		throw new Error(
			exception.message || "Failed to update predictive spend settings",
		);
	}
};
